import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getTemplateDetails } from '../../../../templates-selector';
import { useDispatch } from 'react-redux';
import { EventPopoverContent } from '../../../../../common/styled';
import { Button, Popover, Modal } from 'antd';
import { setEditedExam } from '../../../../templates-actions';
import { removeSlotFromSession } from '../../../../templates-actions';

export const CalendarEventWrapper = (eventWrapperProps) => {
  const dispatch = useDispatch();
  const { children, event, label, readOnly = false } = eventWrapperProps;
  const [showConfirm, setShowConfirm] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const templateDetails = useSelector(getTemplateDetails);

  // If this is a new slot, allow popover for adding
  const isNewSlot = typeof event?.title === 'string' && event.title.trim().toLowerCase() === 'new slot';
  // Prefer isNew property if present, fallback to title check
  const isTrulyNewSlot = event?.isNew || isNewSlot;

  const handleDeleteClick = () => setShowConfirm(true);
  const handlePopoverOpen = () => setPopoverVisible(true);
  const handlePopoverClose = () => setPopoverVisible(false);
  const handleConfirmNo = () => setShowConfirm(false);
  // --- Pace calculation utility (copied from NewConfiguration) ---
  function calculatePace(slots, details) {
    if (!slots || slots.length === 0) return 0;
    const filteredSlots = slots.filter((s) => {
      if (!s.examName) return true;
      return s.examName.toLowerCase() !== 'lunch';
    });
    function parseAmPmTime(str) {
      if (!str) return NaN;
      const match = str.match(/^\d{1,2}:\d{2}\s*([AP]M)$/i) ? str.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i) : null;
      if (!match) return NaN;
      let [, h, m, period] = match;
      h = parseInt(h, 10);
      m = parseInt(m, 10);
      if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
      if (period.toUpperCase() === 'AM' && h === 12) h = 0;
      return h * 60 + m;
    }
    const startTime = details?.startAt;
    const endTime = details?.endAt;
    const startMins = parseAmPmTime(startTime);
    const endMins = parseAmPmTime(endTime);
    let hours = (endMins - startMins) / 60;
    if (isNaN(hours) || hours <= 0) hours = 1;
    const pace = filteredSlots.length / hours;
    return Number(pace.toFixed(1));
  }

  const handleConfirmYes = () => {
    // Remove slot and recalculate pace and examsNameCount in all modes
    let updatedExamsConfiguration = [];
    if (templateDetails && Array.isArray(templateDetails.examsConfiguration)) {
      // Use a more robust check: match examId, from, and to (if present)
      updatedExamsConfiguration = templateDetails.examsConfiguration.filter((exam) => {
        // If examId is unique and present, use it
        if (event?.item?.examId && exam.examId && exam.examId !== event?.item?.examId) return true;
        // If examId is not unique or missing, match by time fields as fallback
        if (
          exam.examId === event?.item?.examId &&
          ((exam.from && event?.item?.from && exam.from !== event?.item?.from) ||
            (exam.to && event?.item?.to && exam.to !== event?.item?.to))
        ) {
          return true;
        }
        // If all match, this is the one to delete
        if (
          exam.examId === event?.item?.examId &&
          (!exam.from || !event?.item?.from || exam.from === event?.item?.from) &&
          (!exam.to || !event?.item?.to || exam.to === event?.item?.to)
        ) {
          return false; // remove this one
        }
        // Otherwise, keep
        return true;
      });
    } else {
      updatedExamsConfiguration = [];
    }
    // Recalculate examsNameCount
    const nameCountMap = {};
    updatedExamsConfiguration.forEach((exam) => {
      const name = exam.examName;
      if (!name) return;
      nameCountMap[name] = (nameCountMap[name] || 0) + 1;
    });
    const examsNameCount = Object.entries(nameCountMap).map(([examName, count]) => ({ examName, count }));
    // Recalculate pace
    const pace = calculatePace(updatedExamsConfiguration, templateDetails);
    // Always update template details in Redux so count updates in create mode too
    dispatch(removeSlotFromSession(event?.item));
    dispatch({
      type: 'UPDATE_TEMPLATE_DETAILS',
      payload: {
        examsConfiguration: updatedExamsConfiguration,
        examsNameCount,
        pace,
      },
    });
    setShowConfirm(false);
    setPopoverVisible(false); // Close popover after deletion
  };

  const content = (
    <EventPopoverContent>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div>{event.title}</div>
          {event?.isModified && (
            <div style={{ background: '#d4d4d0ff', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>Modified</div>
          )}
          {(event?.isNew || event?.isNewSession) && (
            <div style={{ background: '#d4d4d0ff', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>New</div>
          )}
        </div>
        <div>{label}</div>
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {!isTrulyNewSlot && (
          <>
            <Button
              type='link'
              style={{ padding: 0 }}
              onClick={() => {
                // When starting an edit, remove the slot from the visible templateDetails
                // and set it as the editedExam in Redux. This prevents duplicate slots
                // when the user cancels the edit and allows restoring.
                dispatch(removeSlotFromSession(event?.item || event));
                dispatch(setEditedExam(event?.item || event));
              }}
            >
              <img src='./icons/edit-dark.svg' alt='edit' style={{ margin: 0 }} />
            </Button>
            <Button type='link' style={{ padding: 0 }} onClick={handleDeleteClick}>
              <img src='./icons/delete-dark.svg' alt='delete' style={{ margin: 0 }} />
            </Button>
          </>
        )}
      </div>
      <Modal
        open={showConfirm}
        centered
        title='Confirmation'
        onCancel={handleConfirmNo}
        footer={[
          <Button key='no' onClick={handleConfirmNo}>
            No
          </Button>,
          <Button key='yes' type='primary' onClick={handleConfirmYes}>
            Yes
          </Button>,
        ]}
      >
        Are you sure to remove the current slot from the template?
      </Modal>
    </EventPopoverContent>
  );

  return (
    <Popover
      content={content}
      trigger={readOnly ? [] : 'click'}
      open={readOnly ? false : popoverVisible}
      onOpenChange={(visible) => !readOnly && setPopoverVisible(visible)}
    >
      {React.cloneElement(React.Children.only(children), {
        ...children.props,
        className: 'rbc-event custom-event',
        key: event.id,
        onClick: readOnly ? undefined : handlePopoverOpen,
      })}
    </Popover>
  );
};
